import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import SelectAgeGroup from "~components/SelectAgeGroup";
import SelectDiscipline from "~components/SelectDiscipline/SelectDiscipline.tsx";
import SelectEntityStandard from "~components/SelectEntityStandard/SelectEntityStandard.tsx";
import SelectEntityStatus from "~components/SelectEntityStatus/SelectEntityStatus.tsx";
import SelectGender from "~components/SelectGender";
import { TableFilter } from "~enums/tableFilter.ts";
import { SeasonEntityTableFilters } from "~pages/SeasonEntities/SeasonEntityListPage/enums/seasonEntityTableFilters.ts";
import type { LabelValue } from "~types/labelValue.ts";

type SeasonEntityFilters = {
  [SeasonEntityTableFilters.Gender]?: LabelValue;
  [SeasonEntityTableFilters.AgeGroup]?: LabelValue;
  [SeasonEntityTableFilters.Standard]?: LabelValue;
  [SeasonEntityTableFilters.Discipline]?: LabelValue;
  [SeasonEntityTableFilters.Status]?: LabelValue;
};

export const useGetSeasonEntityTableFilters = (): {
  filters: DataTableFilter[];
  currentFilter: SeasonEntityFilters;
  handleFilterChange: (updatedFilter: Record<string, unknown>) => void;
} => {
  const [currentFilter, setCurrentFilter] = useState<SeasonEntityFilters>({});
  const { formatMessage } = useIntl();

  const handleFilterChange = useCallback((updatedFilter: Record<string, unknown>) => {
    setCurrentFilter(updatedFilter as SeasonEntityFilters);
  }, []);

  const filters: DataTableFilter[] = [
    {
      filterKey: SeasonEntityTableFilters.Gender,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectGender onChange={onChange} placeholder={formatMessage({ id: "dataTable.filters.gender.allOption" })} value={value} />
      ),
    },
    {
      filterKey: SeasonEntityTableFilters.AgeGroup,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectAgeGroup onChange={onChange} placeholder={formatMessage({ id: "dataTable.filters.ageGroup.allOption" })} value={value} />
      ),
    },
    {
      filterKey: SeasonEntityTableFilters.Standard,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectEntityStandard
          onChange={onChange}
          placeholder={formatMessage({ id: "dataTable.filters.standard.allOption" })}
          value={value}
        />
      ),
    },
    {
      filterKey: SeasonEntityTableFilters.Discipline,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectDiscipline onChange={onChange} placeholder={formatMessage({ id: "dataTable.filters.discipline.allOption" })} value={value} />
      ),
    },
    {
      filterKey: SeasonEntityTableFilters.Status,
      tableFilterType: TableFilter.Primary,
      filterElement: ({ value, onChange }) => (
        <SelectEntityStatus onChange={onChange} placeholder={formatMessage({ id: "dataTable.filters.status.allOption" })} value={value} />
      ),
    },
  ];

  return {
    currentFilter,
    filters,
    handleFilterChange,
  };
};
